import { Component, OnInit } from '@angular/core';
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { DashboardService } from '../../services/dashboard-service/dashboard.service';
import { SettingsService } from '../../services/setting-service/setting.service';
import { FormControl } from '@angular/forms';
import moment from 'moment';
import 'moment/locale/pt-br';
import { statusOptions, ufOptions, kitOptions } from '../../models/index';

Chart.register(...registerables, ChartDataLabels);

// Cores do sistema de design
const PRIMARY = '#b90066';
const TEAL    = '#009DAE';
const GRAY    = '#5A646E';


@Component({
  selector: 'app-dashboard',
  standalone: false,
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss',
})
export class Dashboard implements OnInit {
  statusColor = {
    'pendente': '#F39C12',
    'aprovado': '#1E8449',
    'processamento': '#2471A3',
    'enviado': '#2471A3',
    'entregue': '#2ECC71',
    'cancelado': '#E74C3C',
    'default': '#5A646E'
  }
  dashboard: any
  rankingTop10Especialidades: any;
  dashboardFiltered: any
  dashboardFull: any

  // Filtros
  dataInicio       = new FormControl();
  dataFim          = new FormControl();
  selectedStatuses = new FormControl();
  selectedUfs      = new FormControl();
  selectedKits     = new FormControl();

  loading: boolean = false;
  filtersExpanded = true;
  isLoading       = false;
  params: any = {};
  paramsInitialStates: any = {};

  statusOptions: any = statusOptions;
  ufOptions: any     = ufOptions;
  kitOptions: any    = kitOptions;

  filteredStats = { totalEntregas: 0, totalCancelamentos: 0, totalAssinaturas: 0, medicosAtivos: 0, medicosInativos: 0 };

  // Dados regionais expostos ao template (aguardando endpoint de região)
  regionData: Array<{ name: string; ufs: string; value: number; color: string; pct: number }> = [];

  // Flags de disponibilidade de dados por visão
  hasUfData        = true;   // usando dados derivados de dashboardFiltered
  hasKitData       = false;  // aguardando: GET /dashboard/por-kit
  hasRegionData    = false;  // aguardando: GET /dashboard/por-regiao
  hasSpecialtyData = false;  // aguardando: GET /dashboard/por-especialidade
  hasStatusData    = true;   // usando dados derivados de dashboardFiltered

  totalMedicosAcumulado: number = 0;
  totalPedidosAcumulado: number = 0;

  private charts: Record<string, Chart> = {};

  private cumulativeDoctorsCreated = false;
  private cumulativeOrdersCreated = false;

  // ── Formatação ────────────────────────────────────────────────
  formatNumber(value: number): string {
    return (value || 0).toLocaleString('pt-BR');
  }

  hasActiveFilters(): boolean {
    return !!(
      this.dataInicio.value ||
      this.dataFim.value ||
      (this.selectedStatuses.value && this.selectedStatuses.value.length > 0) ||
      (this.selectedUfs.value      && this.selectedUfs.value.length > 0) ||
      (this.selectedKits.value     && this.selectedKits.value.length > 0)
    );
  }

  get hasMonthlyData(): boolean {
    return this.dashboardFiltered && (Array.isArray(this.dashboardFiltered) && this.dashboardFiltered.length > 0 || this.dashboardFiltered.resgates_por_estado);
  }

  // Dados da Visão Cumulativa
  get cumulativeData() {
    return this.dashboardFull || [];
  }

  get hasCumulativeData(): boolean {
    return this.cumulativeData && this.cumulativeData.length > 0;
  }

  get hasCumulativeDataDoctors(): boolean {
    return this.cumulativeData && this.cumulativeData.length > 0;
  }

  get hasCumulativeDataOrders(): boolean {
    return this.cumulativeData && this.cumulativeData.length > 0;
  }

  // Extrair períodos para a linha do tempo
  getCumulativePeriods(): string[] {
    return this.cumulativeData.map(item => item.periodo);
  }

  // Extrair dados de médicos ativos
  getCumulativeActiveDoctors(): number[] {
    return this.cumulativeData.map(item => item.dados.medicos.ativos);
  }

  // Extrair dados de médicos inativos
  getCumulativeInactiveDoctors(): number[] {
    return this.cumulativeData.map(item => item.dados.medicos.inativos);
  }

  // Extrair dados de resgates totais
  getCumulativeTotalRescues(): number[] {
    return this.cumulativeData.map(item => item.dados.resgates.total);
  }

  // Extrair dados de resgates entregues
  getCumulativeDeliveredRescues(): number[] {
    return this.cumulativeData.map(item => item.dados.resgates.entregues);
  }

  // Extrair dados de resgates aprovados
  getCumulativeApprovedRescues(): number[] {
    return this.cumulativeData.map(item => item.dados.resgates.aprovados);
  }

  // Extrair dados de resgates em análise
  getCumulativeAnalysisRescues(): number[] {
    return this.cumulativeData.map(item => item.dados.resgates.analise);
  }

  // Extrair dados de resgates enviados
  getCumulativeSentRescues(): number[] {
    return this.cumulativeData.map(item => item.dados.resgates.enviados);
  }

  // Extrair dados de resgates cancelados
  getCumulativeCanceledRescues(): number[] {
    return this.cumulativeData.map(item => item.dados.resgates.cancelados);
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.dataInicio.value)                              count++;
    if (this.dataFim.value)                                 count++;
    if (this.selectedStatuses.value?.length > 0)            count++;
    if (this.selectedUfs.value?.length > 0)                 count++;
    if (this.selectedKits.value?.length > 0)                count++;
    return count;
  }

  get periodLabel(): string {
    const inicio = this.dataInicio.value;
    const fim    = this.dataFim.value;
    if (!inicio && !fim) return '';
    const fmt = (d: Date) => moment(d).locale('pt-br').format('MMM/YYYY');
    if (inicio && fim) return `${fmt(inicio)} – ${fmt(fim)}`;
    if (inicio) return `a partir de ${fmt(inicio)}`;
    return `até ${fmt(fim)}`;
  }

  getCompletionRate(): number {
    const total = this.filteredStats.totalAssinaturas + this.filteredStats.totalCancelamentos;
    if (total === 0) return 0;
    return Math.round((this.filteredStats.totalAssinaturas / total) * 100);
  }

  toggleFilters() { this.filtersExpanded = !this.filtersExpanded; }

  // ── Lifecycle ─────────────────────────────────────────────────
  constructor(public settingsService: SettingsService, public dashboardService: DashboardService) {
    this.settingsService.title = 'Dashboard';
    const hoje = new Date();
    this.dataInicio.setValue(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
    this.dataFim.setValue(hoje);
  }

  async ngOnInit() {}

  async ngAfterViewInit() {
    this.totalMedicosAcumulado = 0;
    this.totalPedidosAcumulado = 0;

    this.settingsService.title = 'Dashboard';
    this.dashboard = await this.dashboardService.getDashboardInfo() || [];

    const dataInicio = this.dataInicio.value || new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const dataFim    = this.dataFim.value    || new Date();
    this.paramsInitialStates = {
      dataInicio: moment(dataInicio).format('YYYY-MM-DD'),
      dataFim:    moment(dataFim).format('YYYY-MM-DD'),
      status: this.selectedStatuses.value || [],
      ufs:    this.selectedUfs.value     || [],
      kits:   this.selectedKits.value    || []
    };

    await this.getDashboardFull();
    await this.getDashboardFiltered(this.paramsInitialStates)

    console.log('DADOS ACUMULADOS:' + JSON.stringify(this.dashboardFull))

    setTimeout(() => {
      this.renderAllCharts();
      this.createCumulativeDoctorsChart(); // Montado apenas uma vez com dados do dashboardFull
      this.createCumulativeOrdersChart();  // Montado apenas uma vez com dados do dashboardFull
    }, 100);
  }

  async getDashboardFull() {
    try {
      this.isLoading = true;
      let data: any = await this.dashboardService.getDashboardAccumulated();
      this.dashboardFull = data.result;
      this.totalMedicosAcumulado = data.totalMedicos.total;
      this.totalPedidosAcumulado = data.totalPedidos.total;
      console.log('DASHBOARD COMPLETO:' + JSON.stringify(this.dashboardFull))
    } catch (error) {
      console.error('Erro ao buscar dashboard completo:', error);
    } finally {
      this.isLoading = false;
    }

  }

  async getDashboardFiltered(params: any) {
    try {
      this.isLoading = true;
      const data: any = await this.dashboardService.getDashboardInfo(params);
      this.dashboardFiltered = data.result;
      this.rankingTop10Especialidades = data.resultTop10;
      // console.log('DASHBOARD FILTRADO:' + JSON.stringify(this.dashboardFiltered.result))
      // console.log('DASHBOARD FILTRADO:' + JSON.stringify(this.dashboardFiltered.resultTop10))

      // Atualizar gráficos da Visão Geral após carregar dados
      this.updateCharts();
    } catch (error) {
      console.error('Erro ao buscar dashboard filtrado:', error);
    } finally {
      this.isLoading = false;
    }

  }

  // ── Stats derivados da API ────────────────────────────────────
  initializeStats() {
    if (!this.dashboardFiltered) {
      this.filteredStats = { totalEntregas: 0, totalCancelamentos: 0, totalAssinaturas: 0, medicosAtivos: 0, medicosInativos: 0 };
      return;
    }

    let totalEntregas = 0;
    let totalCancelamentos = 0;

    if (this.dashboardFiltered.resgates_por_estado) {
      this.dashboardFiltered.resgates_por_estado.forEach((uf: any) => {
        totalEntregas += uf.resgates.entregues || 0;
        totalCancelamentos += uf.resgates.cancelados || 0;
      });
    }

    this.filteredStats = {
      totalEntregas: totalEntregas,
      totalCancelamentos: totalCancelamentos,
      totalAssinaturas: parseInt(this.dashboardFiltered.total_medicos_ativos) || 0,
      medicosAtivos: parseInt(this.dashboardFiltered.total_medicos_ativos) || 0,
      medicosInativos: parseInt(this.dashboardFiltered.total_medicos_inativos) || 0
    };
  }

  // ── Extrações da API mensal ───────────────────────────────────
  monthLabels(): string[] {
    if (!this.dashboardFiltered?.resgates_por_estado) return [];
    return ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  }

  monthlyEntregas(): number[] {
    if (!this.dashboardFiltered?.resgates_por_estado) return [];
    return Array(12).fill(0);
  }

  monthlyCancelamentos(): number[] {
    if (!this.dashboardFiltered?.resgates_por_estado) return [];
    return Array(12).fill(0);
  }

  monthlyAssinaturas(): number[] {
    if (!this.dashboardFiltered?.resgates_por_estado) return [];
    return Array(12).fill(0);
  }

  monthlyCompletionPct(): number[] {
    if (!this.dashboardFiltered?.resgates_por_estado) return [];
    return Array(12).fill(0);
  }


  // ── Filtros ───────────────────────────────────────────────────
  async applyFilters() {
    console.log('🔍 applyFilters() iniciado');
    console.log('cumulativeDoctorsCreated antes:', this.cumulativeDoctorsCreated);
    console.log('cumulativeOrdersCreated antes:', this.cumulativeOrdersCreated);
    console.log('charts cumulativeDoctorsChart:', !!this.charts['cumulativeDoctorsChart']);
    console.log('charts cumulativeOrdersChart:', !!this.charts['cumulativeOrdersChart']);

    this.isLoading = true;

    const dataInicio = this.dataInicio.value || new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const dataFim    = this.dataFim.value    || new Date();
    this.params = {
      dataInicio: moment(dataInicio).format('YYYY-MM-DD'),
      dataFim:    moment(dataFim).format('YYYY-MM-DD'),
      status: this.selectedStatuses.value || [],
      ufs:    this.selectedUfs.value     || [],
      kits:   this.selectedKits.value    || []
    };

    await this.getDashboardFiltered(this.params);

    console.log('🔍 applyFilters() finalizado');
    console.log('cumulativeDoctorsCreated depois:', this.cumulativeDoctorsCreated);
    console.log('cumulativeOrdersCreated depois:', this.cumulativeOrdersCreated);
  }

  async clearFilters() {
    this.selectedStatuses.setValue([]);
    this.selectedUfs.setValue([]);
    this.selectedKits.setValue([]);

    // Recarregar dados sem filtros (updateCharts será chamado dentro do getDashboardFiltered)
    await this.getDashboardFiltered(this.paramsInitialStates);
  }

  updateCharts() {
    this.isLoading = true;
    setTimeout(() => {
      this.renderAllCharts(); // Não inclui createCumulativeChart - montado apenas uma vez

      // FORÇAR recriação dos gráficos cumulativos com dados do dashboardFull
      this.forceRecreateCumulativeCharts();

      this.isLoading = false;
    }, 100);
  }

  // Forçar recriação dos gráficos cumulativos (mas com dados do dashboardFull)
  private forceRecreateCumulativeCharts() {
    console.log('🔥 Forçando recriação dos gráficos cumulativos...');

    // Destruir gráficos antigos se existirem
    if (this.charts['cumulativeDoctorsChart']) {
      this.charts['cumulativeDoctorsChart'].destroy();
      delete this.charts['cumulativeDoctorsChart'];
    }

    if (this.charts['cumulativeOrdersChart']) {
      this.charts['cumulativeOrdersChart'].destroy();
      delete this.charts['cumulativeOrdersChart'];
    }

    // Resetar flags para permitir recriação
    this.cumulativeDoctorsCreated = false;
    this.cumulativeOrdersCreated = false;

    // Recriar gráficos cumulativos com dados do dashboardFull
    this.createCumulativeDoctorsChart();
    this.createCumulativeOrdersChart();
  }

  // Método removido - substituído por forceRecreateCumulativeCharts()


  // ── Helpers para chips de filtros ─────────────────────────────
  getStatusLabel(val: any): string {
    return this.statusOptions.find((s: any) => s.value === val)?.description || String(val);
  }

  getKitLabel(val: any): string {
    return this.kitOptions.find((k: any) => k.value === val)?.description || String(val);
  }

  async removeStatusFilter(val: any) {
    const current: any[] = this.selectedStatuses.value || [];
    this.selectedStatuses.setValue(current.filter(v => v !== val));
    await this.applyFilters();
  }

  async removeUfFilter(val: any) {
    const current: any[] = this.selectedUfs.value || [];
    this.selectedUfs.setValue(current.filter(v => v !== val));
    await this.applyFilters();
  }

  async removeKitFilter(val: any) {
    const current: any[] = this.selectedKits.value || [];
    this.selectedKits.setValue(current.filter(v => v !== val));
    await this.applyFilters();
  }

  async clearDateFilter() {
    this.dataInicio.setValue(null);
    this.dataFim.setValue(null);
    await this.applyFilters();
  }

  // ── Render all ────────────────────────────────────────────────
  private renderAllCharts() {
    this.createChartDeliveryAndSignature();
    this.createPieChart();
    this.createPercentageChart();
    this.createChartUF();
    this.createRankingEspecialidadesChart();
    this.createChartDeliveryStatus();
    this.createKitBarChart();
    this.createRegionChart();
    this.createSpecialtyChart();
  }

  private destroyChart(id: string) {
    if (this.charts[id]) { this.charts[id].destroy(); delete this.charts[id]; }
  }

  private gridColor() { return 'rgba(44, 50, 56, 0.06)'; }

  // ════════════════════════════════════════════════════════════
  //  VISÃO GERAL — dados reais da API
  // ════════════════════════════════════════════════════════════

  // Entregas por mês (barra vertical) — entregas e cancelamentos em barras, assinaturas como linha no Y2
  createChartDeliveryAndSignature() {
    this.destroyChart('verticalBarChart');
    const ctx = document.getElementById('verticalBarChart') as HTMLCanvasElement;
    if (!ctx) return;

    let totalEntregues = 0;
    let totalCancelados = 0;
    

    console.log('=== DADOS DO GRÁFICO ENTREGAS ===');
    console.log('dashboardFiltered:', this.dashboardFiltered);
    
    if (this.dashboardFiltered?.resgates_por_estado) {
      this.dashboardFiltered.resgates_por_estado.forEach((uf: any) => {
        console.log(`UF ${uf.uf}: entregues=${uf.resgates.entregues}, cancelados=${uf.resgates.cancelados}`);
        totalEntregues += uf.resgates.entregues || 0;
        totalCancelados += uf.resgates.cancelados || 0;
      });
    }

    // const totalPedidos = parseInt(this.dashboardFiltered?.pedidos_total || '0');
    const totalAnalise = parseInt(this.dashboardFiltered?.total_pedidos_analise || '0');
    const totalAprovados = parseInt(this.dashboardFiltered?.total_pedidos_aprovados || '0');
    const totalEnviados = parseInt(this.dashboardFiltered?.total_pedidos_enviados || '0');
    const totalAssinaturas = parseInt(this.dashboardFiltered?.assinaturas_vigentes || '0');

    console.log('TOTAL ENTREGUES:', totalEntregues);
    console.log('TOTAL CANCELADOS:', totalCancelados);
    console.log('TOTAL ASSINATURAS:', totalAssinaturas);
    console.log('EM ANÁLISE:', totalAnalise);
    console.log('EM ANDAMENTO:', totalAprovados);
    console.log('ENVIADOS:', totalEnviados);

    this.charts['verticalBarChart'] = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Totais'],
        datasets: [
          {
            type: 'bar' as any,
            label: 'Em análise',
            data: [totalAnalise],
            backgroundColor: this.statusColor.pendente, borderRadius: 4, borderWidth: 0,
            yAxisID: 'y'
          },
          {
            type: 'bar' as any,
            label: 'Aprovados',
            data: [totalAprovados],
            backgroundColor: this.statusColor.aprovado, borderRadius: 4, borderWidth: 0,
            yAxisID: 'y'
          },
          {
            type: 'bar' as any,
            label: 'Enviados',
            data: [totalEnviados],
            backgroundColor: this.statusColor.enviado, borderRadius: 4, borderWidth: 0,
            yAxisID: 'y'
          },
          {
            type: 'bar' as any,
            label: 'Entregues',
            data: [totalEntregues],
            backgroundColor: this.statusColor.entregue, borderRadius: 4, borderWidth: 0,
            yAxisID: 'y'
          },
          {
            type: 'bar' as any,
            label: 'Cancelados',
            data: [totalCancelados],
            backgroundColor: this.statusColor.cancelado, borderRadius: 4, borderWidth: 0,
            yAxisID: 'y'
          },
          {
            type: 'bar' as any,
            label: 'Assinaturas vigentes',
            data: [totalAssinaturas],
            backgroundColor: PRIMARY, borderRadius: 4, borderWidth: 0,
            yAxisID: 'y'
          }
        ]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'bottom', labels: { color: GRAY, padding: 14, font: { size: 12 } } },
          datalabels: {
            display: (context: any) => context.dataset.data[context.dataIndex] > 0,
            color: '#2c3e50',
            font: { weight: 'bold', size: 14 },
            anchor: 'end',
            align: 'top',
            offset: 5,
            textAlign: 'center',
            formatter: (value: any) => { return value.toString()},
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            borderRadius: 4,
            padding: 4
          }
        },
        scales: {
          x: { grid: { display: false }, ticks: { color: GRAY } },
          y: {
            display: false,
            beginAtZero: true, position: 'left',
            grid: { display: false, color: this.gridColor() }, ticks: { color: GRAY },
            title: { display: true, text: 'Pedidos', color: GRAY, font: { size: 11 } },
            max: Math.max(totalAnalise, totalAprovados, totalEnviados, totalEntregues, totalCancelados, totalAssinaturas) + 0.5
          },
          y2: {
            display: false,
            beginAtZero: true, position: 'right',
            grid: { display: false }, ticks: { color: TEAL },
            title: { display: true, text: 'Médicos ativos', color: TEAL, font: { size: 11 } },
            max: totalAssinaturas + 0.2
          }
        }
      }
    });
  }

  // Status das entregas — usando dados reais de dashboardFiltered
  createPieChart() {
    this.destroyChart('pieChart');
    const ctx = document.getElementById('pieChart') as HTMLCanvasElement;
    if (!ctx) return;

    let entregues = 0;
    let cancelados = 0;
    let emTransito = 0;
    let pendentes = 0;

    if (this.dashboardFiltered?.resgates_por_estado) {
      this.dashboardFiltered.resgates_por_estado.forEach((uf: any) => {
        entregues += uf.resgates.entregues || 0;
        cancelados += uf.resgates.cancelados || 0;
        emTransito += uf.resgates.enviados || 0;
        pendentes += uf.resgates.analise || 0;
      });
    }

    const data = [entregues, emTransito, pendentes, cancelados];

    this.charts['pieChart'] = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: ['Entregues', 'Enviado', 'Pendentes', 'Cancelados'],
        datasets: [{ data, backgroundColor: [
          this.statusColor.entregue,
          this.statusColor.enviado,
          this.statusColor.pendente,
          this.statusColor.cancelado
        ], borderWidth: 3, borderColor: '#fff' }]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom', labels: { color: GRAY, padding: 15, font: { size: 12 } } },
          datalabels: {
            color: '#fff', font: { weight: 'bold', size: 13 },
            formatter: (value: any, ctx: any) => {
              const sum = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
              return sum > 0 ? ((value / sum) * 100).toFixed(1) + '%' : '';
            }
          }
        }
      }
    });
  }

  // % de conclusão por mês — derivado de entregas_realizadas / (entregas + cancelamentos)
  createPercentageChart() {
    this.destroyChart('percentageChart');
    const ctx = document.getElementById('percentageChart') as HTMLCanvasElement;
    if (!ctx) return;

    const pcts = this.monthlyCompletionPct();
    const minY = pcts.length ? Math.max(0, Math.min(...pcts) - 10) : 0;

    const labels = this.monthLabels();
    const referenceLine = labels.map(() => 80);

    // Plugin inline que escreve "Meta 80%" diretamente sobre a linha tracejada
    const metaLabelPlugin = {
      id: 'metaLabel',
      afterDraw(chart: any) {
        if (!chart.data.datasets[1] || !chart.scales['y']) return;
        const c = chart.ctx;
        const xEnd = chart.chartArea.right;
        const yPos = chart.scales['y'].getPixelForValue(80);
        c.save();
        c.font = '11px Arial, sans-serif';
        c.fillStyle = 'rgba(90, 100, 110, 0.65)';
        c.textAlign = 'right';
        c.fillText('Meta 80%', xEnd - 4, yPos - 6);
        c.restore();
      }
    };

    this.charts['percentageChart'] = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: '% Entregas Concluídas',
            data: pcts,
            borderColor: PRIMARY, backgroundColor: 'rgba(185, 0, 102, 0.08)',
            borderWidth: 2.5, fill: true, tension: 0.4,
            // Pontos verdes quando ≥ meta, vermelhos quando abaixo
            pointBackgroundColor: pcts.map(v => v >= 80 ? '#27AE60' : '#E74C3C'),
            pointBorderColor: '#fff', pointBorderWidth: 2, pointRadius: 5
          },
          {
            label: 'Meta 80%',
            data: referenceLine,
            borderColor: 'rgba(90, 100, 110, 0.40)',
            borderWidth: 1.5,
            borderDash: [6, 4],
            fill: false,
            tension: 0,
            pointRadius: 0,
            pointHoverRadius: 0
          } as any
        ]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              color: GRAY,
              font: { size: 12 },
              filter: (item) => item.text !== 'Meta 80%' || referenceLine.length > 0
            }
          },
          datalabels: { display: false }
        },
        scales: {
          y: { beginAtZero: false, min: minY, max: 100, grid: { color: this.gridColor() }, ticks: { color: GRAY, callback: (v) => v + '%' } },
          x: { grid: { display: false }, ticks: { color: GRAY } }
        }
      },
      plugins: [metaLabelPlugin]
    });
  }

  createCumulativeDoctorsChart() {
    if (this.cumulativeDoctorsCreated) return; // Já foi criado
    if (this.charts['cumulativeDoctorsChart']) return; // Se já existe, não recria

    const ctx = document.getElementById('cumulativeDoctorsChart') as HTMLCanvasElement;
    if (!ctx || !this.hasCumulativeDataDoctors) return;

    const existingChart = Chart.getChart(ctx);
    if (existingChart) {
      console.log('Gráfico de médicos já existe, evitando recriação');
      return;
    }

    // Marcar como criado ANTES de criar o gráfico
    this.cumulativeDoctorsCreated = true;

    this.charts['cumulativeDoctorsChart'] = new Chart(ctx, {
      type: 'line',
      data: {
        labels: this.getCumulativePeriods(),
        datasets: [
          {
            label: 'Ativos',
            data: this.getCumulativeActiveDoctors(),
            borderColor: PRIMARY,
            backgroundColor: 'rgba(185, 0, 102, 0.1)',
            borderWidth: 3,
            fill: true,
            tension: 0.3,
            pointBackgroundColor: PRIMARY,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 5
          },
          {
            label: 'Inativos',
            data: this.getCumulativeInactiveDoctors(),
            borderColor: '#5A646E',
            backgroundColor: 'rgba(90, 100, 110, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.3,
            pointBackgroundColor: '#5A646E',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              color: GRAY,
              padding: 16,
              font: { size: 12 },
              usePointStyle: true
            }
          },
          datalabels: { display: false },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: '#333',
            borderWidth: 1,
            padding: 12,
            displayColors: true,
            callbacks: {
              title: (context) => `Período: ${context[0].label}`,
              label: (context) => {
                const label = context.dataset.label || '';
                const value = context.parsed.y;
                return `${label}: ${value}`;
              }
            }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: GRAY,
              font: { size: 11 }
            }
          },
          y: {
            beginAtZero: true,
            grid: {
              color: this.gridColor()
            },
            ticks: {
              color: GRAY,
              font: { size: 11 }
            },
            title: {
              display: true,
              text: 'Quantidade de Médicos',
              color: GRAY,
              font: { size: 12, weight: 'normal' }
            }
          }
        }
      }
    });
  }

  createCumulativeOrdersChart() {
    if (this.cumulativeOrdersCreated) return; // Já foi criado
    if (this.charts['cumulativeOrdersChart']) return; // Se já existe, não recria

    const ctx = document.getElementById('cumulativeOrdersChart') as HTMLCanvasElement;
    if (!ctx || !this.hasCumulativeDataOrders) return;

    const existingChart = Chart.getChart(ctx);
    if (existingChart) {
      console.log('Gráfico de pedidos já existe, evitando recriação');
      return;
    }

    this.cumulativeOrdersCreated = true;

    this.charts['cumulativeOrdersChart'] = new Chart(ctx, {
      type: 'line',
      data: {
        labels: this.getCumulativePeriods(),
        datasets: [
          {
            label: 'Total',
            data: this.getCumulativeTotalRescues(),
            borderColor: this.statusColor.default,
            backgroundColor: 'rgba(0, 157, 174, 0.1)',
            borderWidth: 2.5,
            fill: false,
            tension: 0.4,
            pointBackgroundColor: this.statusColor.default,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 5
          },
          {
            label: 'Entregues',
            data: this.getCumulativeDeliveredRescues(),
            borderColor: this.statusColor.entregue,
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            pointBackgroundColor: this.statusColor.entregue,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 4
          },
          {
            label: 'Em Análise',
            data: this.getCumulativeAnalysisRescues(),
            borderColor: this.statusColor.pendente,
            backgroundColor: 'rgba(255, 152, 0, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            pointBackgroundColor: this.statusColor.pendente,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 4
          },
          {
            label: 'Enviados',
            data: this.getCumulativeSentRescues(),
            borderColor: this.statusColor.enviado,
            backgroundColor: 'rgba(156, 39, 176, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            pointBackgroundColor: this.statusColor.enviado,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 4
          },
          {
            label: 'Cancelados',
            data: this.getCumulativeCanceledRescues(),
            borderColor: this.statusColor.cancelado,
            backgroundColor: 'rgba(231, 76, 60, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.4,
            pointBackgroundColor: this.statusColor.cancelado,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              color: GRAY,
              padding: 16,
              font: { size: 12 },
              usePointStyle: true
            }
          },
          datalabels: { display: false },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: '#333',
            borderWidth: 1,
            padding: 12,
            displayColors: true,
            callbacks: {
              title: (context) => `Período: ${context[0].label}`,
              label: (context) => {
                const label = context.dataset.label || '';
                const value = context.parsed.y;
                return `${label}: ${value}`;
              }
            }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: GRAY,
              font: { size: 11 }
            }
          },
          y: {
            beginAtZero: true,
            grid: {
              color: this.gridColor()
            },
            ticks: {
              color: GRAY,
              font: { size: 11 }
            },
            title: {
              display: true,
              text: 'Quantidade de Pedidos',
              color: GRAY,
              font: { size: 12, weight: 'normal' }
            }
          }
        }
      }
    });
  }

  createChartUF() {
    this.destroyChart('horizontalBarChart');
    const ctx = document.getElementById('horizontalBarChart') as HTMLCanvasElement;
    if (!ctx) return;

    let ufData: Array<{ uf: string; entregas: number }> = [];

    if (this.dashboardFiltered?.resgates_por_estado) {
      ufData = this.dashboardFiltered.resgates_por_estado
        .map((uf: any) => ({
          uf: uf.uf,
          entregas: uf.resgates.total || 0
        }))
        .filter((item: any) => item.entregas > 0)
        .sort((a: any, b: any) => b.entregas - a.entregas)
        .slice(0, 10);
    }

    this.charts['horizontalBarChart'] = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ufData.map(d => d.uf),
        datasets: [{ label: 'Pedidos', data: ufData.map(d => d.entregas), backgroundColor: PRIMARY, borderRadius: 4, borderWidth: 0 }]
      },
      options: {
        indexAxis: 'y', responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          datalabels: {
            anchor: 'end',
            align: 'end',
            color: GRAY,
            font: { weight: 'bold', size: 11 },
            formatter: (value: any, context: any) => {
              const uf = context.chart.data.labels[context.dataIndex] as string;
              return `${value} . ${uf}`;
            }
          }
        },
        scales: {
          x: { 
            display: false, 
            beginAtZero: true, 
            grid: { color: this.gridColor() }, 
            ticks: { 
              color: GRAY,
              stepSize: 1  // pula de 1 em 1
            },
            max: (Math.max(...ufData.map(d => d.entregas)) * 1) + 0.5
          },
          y: { 
            grid: { display: false }, 
            ticks: { 
              display: false,
              color: GRAY,
              padding: 20
            } 
          }
        }
      }
    });
  }

  createRankingEspecialidadesChart() {
    this.destroyChart('rankingEspecialidadesChart');
    const ctx = document.getElementById('rankingEspecialidadesChart') as HTMLCanvasElement;
    if (!ctx) return;

    let especialidadeData: Array<{ especialidade: string; total_pedidos: number }> = [];

    if (this.rankingTop10Especialidades) {
      especialidadeData = this.rankingTop10Especialidades
    }

    this.charts['rankingEspecialidadesChart'] = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: especialidadeData.map(d => d.especialidade),
        datasets: [{ label: 'Pedidos', data: especialidadeData.map(d => d.total_pedidos), backgroundColor: PRIMARY, borderRadius: 4, borderWidth: 0 }]
      },
      options: {
        indexAxis: 'y', responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          datalabels: { 
            anchor: 'end', 
            align: 'end', 
            color: GRAY, 
            font: { weight: 'bold', size: 11 },
            formatter: (value: any, context: any) => {
              const especialidade = context.chart.data.labels[context.dataIndex];
              return `${value} . ${especialidade}`;
            }
          }
        },
        scales: {
          x: { 
            display: false, 
            beginAtZero: true, 
            grid: { color: this.gridColor() }, 
            ticks: { color: GRAY, stepSize: 1 },
            max: especialidadeData.length > 0 ? (Math.max(...especialidadeData.map(d => d.total_pedidos)) * 1) + 0.75 : 10
          },
          y: { 
            grid: { display: false }, 
            ticks: { 
              display: false
            }
          }
        }
      }
    });
  }
  
  createChartDeliveryStatus() {
    this.destroyChart('kitDonutChart');
    const ctx = document.getElementById('kitDonutChart') as HTMLCanvasElement;
    if (!ctx) return;

    // TODO: substituir por dados reais quando endpoint estiver disponível
    // const kitTotals = this.kitBreakdown; // [{ kit_nome, total }]
    const kitTotals: Array<{ kit_nome: string; total: number }> = [];

    this.charts['kitDonutChart'] = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: kitTotals.map(k => k.kit_nome),
        datasets: [{
          data: kitTotals.map(k => k.total),
          backgroundColor: [PRIMARY, TEAL, '#F4A623', '#4CAF50'],
          borderWidth: 3, borderColor: '#fff'
        }]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom', labels: { color: GRAY, padding: 20, font: { size: 13 } } },
          datalabels: {
            color: '#fff', font: { weight: 'bold', size: 14 },
            formatter: (value: any, ctx: any) => {
              const sum = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
              return sum > 0 ? ((value / sum) * 100).toFixed(1) + '%' : '';
            }
          }
        }
      }
    });
  }

  createKitBarChart() {
    this.destroyChart('kitBarChart');
    const ctx = document.getElementById('kitBarChart') as HTMLCanvasElement;
    if (!ctx) return;

    // TODO: substituir por dados reais quando endpoint estiver disponível
    // Agrupar this.kitMonthlyBreakdown por kit_id e gerar um dataset por kit
    const labels: string[]   = [];
    const datasets: any[]    = [];

    this.charts['kitBarChart'] = new Chart(ctx, {
      type: 'bar',
      data: { labels, datasets },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom', labels: { color: GRAY, padding: 16, font: { size: 12 } } },
          datalabels: { display: false }
        },
        scales: {
          x: { grid: { display: false }, ticks: { color: GRAY } },
          y: { beginAtZero: true, grid: { color: this.gridColor() }, ticks: { color: GRAY } }
        }
      }
    });
  }

  public buildRegionData(apiData: Array<{ regiao: string; ufs: string; total: number }> = []) {
    const colors = [PRIMARY, TEAL, '#F4A623', '#4CAF50', '#9B59B6'];
    const total  = apiData.reduce((s, r) => s + r.total, 0);
    return apiData.map((r, i) => ({
      name:  r.regiao,
      ufs:   r.ufs,
      value: r.total,
      color: colors[i % colors.length],
      pct:   total > 0 ? Math.round((r.total / total) * 100) : 0
    }));
  }

  createRegionChart() {
    this.destroyChart('regionChart');
    const ctx = document.getElementById('regionChart') as HTMLCanvasElement;
    if (!ctx) return;

    // TODO: substituir por dados reais quando endpoint estiver disponível
    // this.regionData = this.buildRegionData(this.regionBreakdown);
    this.regionData = [];

    this.charts['regionChart'] = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: this.regionData.map(r => r.name),
        datasets: [{
          label: 'Entregas',
          data: this.regionData.map(r => r.value),
          backgroundColor: this.regionData.map(r => r.color),
          borderRadius: 6, borderWidth: 0
        }]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          datalabels: { anchor: 'end', align: 'end', color: GRAY, font: { weight: 'bold', size: 12 }, formatter: (v: number) => v.toLocaleString('pt-BR') }
        },
        scales: {
          x: { grid: { display: false }, ticks: { color: GRAY } },
          y: { beginAtZero: true, grid: { color: this.gridColor() }, ticks: { color: GRAY } }
        }
      }
    });
  }

  createSpecialtyChart() {
    this.destroyChart('specialtyChart');
    const ctx = document.getElementById('specialtyChart') as HTMLCanvasElement;
    if (!ctx) return;

    // TODO: substituir por dados reais quando endpoint estiver disponível
    // const specialties = this.specialtyBreakdown.slice(0, 8);
    const specialties: Array<{ label: string; value: number }> = [];

    this.charts['specialtyChart'] = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: specialties.map(s => s.label),
        datasets: [{
          label: 'Solicitações',
          data: specialties.map(s => s.value),
          backgroundColor: specialties.map((_, i) =>
            i === 0 ? PRIMARY : i === 1 ? TEAL : `rgba(185, 0, 102, ${Math.max(0.3, 0.75 - i * 0.08)})`
          ),
          borderRadius: 4, borderWidth: 0
        }]
      },
      options: {
        indexAxis: 'y', responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          datalabels: { anchor: 'end', align: 'end', color: GRAY, font: { weight: 'bold', size: 12 } }
        },
        scales: {
          x: { beginAtZero: true, grid: { color: this.gridColor() }, ticks: { color: GRAY } },
          y: { grid: { display: false }, ticks: { color: GRAY } }
        }
      }
    });
  }

  //  CHARTS LEGADOS (mantidos para compatibilidade com canvas ocultos)

  activeUsersChart() {
    const ctx = document.getElementById('usersChart') as HTMLCanvasElement;
    if (!ctx || !this.hasMonthlyData) return;
    const ativos   = this.dashboard.reduce((s: number, i: any) => s + (parseInt(i.medicos_ativos)   || 0), 0);
    const inativos = this.dashboard.reduce((s: number, i: any) => s + (parseInt(i.medicos_inativos) || 0), 0);
    new Chart(ctx, {
      type: 'doughnut',
      data: { labels: ['Ativos','Inativos'], datasets: [{ label: 'Médicos', data: [ativos, inativos], backgroundColor: ['rgba(123,248,123,1)', 'rgb(255,99,132)'], hoverOffset: 4 }] },
      options: { plugins: { title: { display: true, text: 'Médicos ativos e inativos' }, datalabels: { color: '#101111', font: { weight: 'bold', size: 14 }, formatter: (v: any) => v } } }
    });
  }

  barChart() {
    const ctx = document.getElementById('barChart') as HTMLCanvasElement;
    if (!ctx || !this.hasMonthlyData) return;
    new Chart(ctx, {
      type: 'bar',
      data: {
        labels: this.monthLabels(),
        datasets: [
          { label: 'Entregas realizadas', data: this.monthlyEntregas(),    backgroundColor: ['rgba(43,175,71,0.2)'],  borderColor: ['rgba(116,206,32,1)'], borderWidth: 1 },
          { label: 'Cancelamentos',       data: this.monthlyCancelamentos(), backgroundColor: ['rgba(255,99,132,0.2)'], borderColor: ['rgba(255,99,132,1)'], borderWidth: 1 },
          { label: 'Assinaturas',         data: this.monthlyAssinaturas(),  backgroundColor: ['rgba(14,31,182,0.2)'],  borderColor: ['rgba(71,38,150,1)'],  borderWidth: 1 }
        ]
      },
      options: { plugins: { datalabels: { color: '#101111', font: { weight: 'bold', size: 14 } } }, scales: { y: { beginAtZero: true } } }
    });
  }
}
